import { LitElement, html } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';

import '@amityeko/design-system/dist/providers/notification';

import '../../components/image-layout';
import '../../components/badges-dashboard';
import '../../components/image-layout-list';
import '../../components/create-recognition';
import '../../components/create-recognition-preview';

import RnRImageLayout from '../../components/image-layout';
import RnRBadgesDashboard from '../../components/badges-dashboard';
import RnRImageLayoutList from '../../components/image-layout-list';
import RnRCreateRecognition from '../../components/create-recognition';
import RnRCreateRecognitionPreview from '../../components/create-recognition-preview';

/**
 * The element can be triggered in two ways:
 *   - by calling show() API
 *   - by placing a clickable element inside the element
 *
 * @slot default - Element that triggers the recognition creation flow.
 *
 * @event {{ recognitionId }} rnr-client-recognition-created - Emitted when a new recognition
 *        is successfully created.
 */
@customElement('rnr-page-create-recognition')
export default class RnRPageCreateRecognition extends LitElement {
  @query('#badge_dashboard') badgeDashboard!: RnRBadgesDashboard;
  @query('#create_recognition') createRecognition!: RnRCreateRecognition;
  @query('#create_recognition_preview') createRecognitionPreview!: RnRCreateRecognitionPreview;
  @query('#image_layout_list') imageLayoutList!: RnRImageLayoutList;
  @query('#image_layout') imageLayout!: RnRImageLayout;

  @property({ attribute: 'receiver-id' })
  receiverId?: string;

  @property({ attribute: 'receiver-display-name' })
  receiverDisplayName?: string;

  @property({ type: Number, attribute: 'preview-scale' })
  previewScale?: number;

  /** Starts the recognition creation flow. */
  public show() {
    this.badgeDashboard.show();
  }

  private _onClickContinue(e: CustomEvent) {
    this.createRecognition.show(e?.detail);
  }

  private _onCreateRecognitionBack() {
    this.badgeDashboard.show();
  }

  private _onCreateRecognitionConfirm(e: CustomEvent) {
    this.createRecognitionPreview.show(e?.detail);
  }

  private _onCreateRecognitionPreviewBack() {
    this.createRecognition.show();
  }

  private _onCreateRecognitionSuccess(e: CustomEvent) {
    emit(this, 'rnr-client-recognition-created', {
      detail: { recognitionId: e.detail?.recognitionId },
    });
    this.createRecognition.reset();
  }

  private _onCreateRecognitionReset() {
    this.createRecognition.reset();
  }

  private _onUploadImage() {
    this.imageLayoutList.show();
  }

  private _onUploadImageCancel() {
    this.imageLayout.reset();
  }

  private _onSelectImageLayout(e: CustomEvent) {
    this.imageLayout.show(e?.detail.layout);
  }

  private _onBackImageLayoutList() {
    this.createRecognition.show();
  }

  private _onImageLayoutConfirm(e: CustomEvent) {
    this.createRecognition.show({}, e?.detail);
  }

  private _onImageLayoutBack() {
    this.imageLayoutList.show();
  }

  private _onClickTrigger(e: Event) {
    // Stops any default behavior of the slotted trigger.
    e.preventDefault();
    e.stopPropagation();

    this.show();
  }

  protected render() {
    return html`
      <amityeko-notification-provider>
        <rnr-badges-dashboard id="badge_dashboard" @rnr-click-continue=${this._onClickContinue}>
          <slot @click=${this._onClickTrigger}></slot>
        </rnr-badges-dashboard>

        <rnr-create-recognition
          id="create_recognition"
          @rnr-create-recognition-dialog-back=${this._onCreateRecognitionBack}
          @rnr-click-button-preview=${this._onCreateRecognitionConfirm}
          @rnr-click-upload-image=${this._onUploadImage}
          @rnr-click-upload-image-cancel=${this._onUploadImageCancel}
        ></rnr-create-recognition>

        <rnr-create-recognition-preview
          id="create_recognition_preview"
          .receiverId=${this.receiverId}
          .receiverDisplayName=${this.receiverDisplayName}
          .previewScale=${this.previewScale}
          @rnr-create-recognition-preview-dialog-back=${this._onCreateRecognitionPreviewBack}
          @rnr-create-recognition-preview-recognition-created=${this._onCreateRecognitionSuccess}
          @rnr-edit-quit-dialog=${this._onCreateRecognitionReset}
        ></rnr-create-recognition-preview>

        <rnr-image-layout-list
          id="image_layout_list"
          @rnr-select-layout=${this._onSelectImageLayout}
          @rnr-image-layout-list-back=${this._onBackImageLayoutList}
        ></rnr-image-layout-list>

        <rnr-image-layout
          id="image_layout"
          @rnr-confirm-image-layout=${this._onImageLayoutConfirm}
          @rnr-image-layout-back=${this._onImageLayoutBack}
        ></rnr-image-layout>
      </amityeko-notification-provider>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-page-create-recognition': RnRPageCreateRecognition;
  }
}
